import type { SessionId } from "@don/wire";

import type { StepAt } from "../session/reading.js";

/**
 * The Knowledge seam (ux-spec decision 38, PRD module 7's null-means-absent pattern).
 *
 * The typed port the session's side column reads: its Knowledge and Workflows sections. The
 * derived route fills it (`Backend.knowledge`, chosen by `knowledgeSources`); the dev handle's
 * `applyKnowledge` is how a test or the visual gate hands in a source instead. The org-wide read
 * the Knowledge hub had went with the hub (issue 286).
 *
 * Every string here is free text from the source: the Console renders it as text only, never
 * as markup and never as a link target (issue 202 security P2-2; `KnowledgeItem`,
 * `WorkflowItem`). The only link a knowledge place draws is to a session, built from its id by
 * `sessionPath`.
 */

/** Whether a knowledge item has been kept by the owner or is still a draft. */
export type KnowledgeStatus = "draft" | "kept";

/**
 * Where an item's evidence is: a time in the recording, or a bookend (`wire::Evidence`, issue
 * 229). The same shape as a Summary step's place, printed by the same `atText`.
 */
export type KnowledgeEvidence = StepAt;

/** One piece of knowledge drawn out of a session. */
export interface KnowledgeItem {
  readonly id: string;
  /** The kind's word, e.g. `Rule`, `Gotcha`, `Where to find`, `Other`. */
  readonly kind: string;
  readonly statement: string;
  readonly status: KnowledgeStatus;
  /** The moments it rests on: positions in the recording, or the intro or outro. */
  readonly evidence: readonly KnowledgeEvidence[];
}

/** One step of a workflow, with the app it happened in and the moment it shows. */
export interface WorkflowStep {
  readonly text: string;
  readonly app: string | null;
  readonly atMs: number | null;
}

export interface Workflow {
  readonly id: string;
  readonly name: string;
  readonly goal: string | null;
  readonly steps: readonly WorkflowStep[];
}

/**
 * What a source says about one session: still being written, did not finish (`failed`, issue
 * 229), not written for this session (`absent`: the Summary it comes from is absent by design),
 * or its items and workflows.
 */
export type SessionKnowledge =
  | { readonly state: "pending" }
  | { readonly state: "failed" }
  | { readonly state: "absent" }
  | {
      readonly state: "answered";
      readonly items: readonly KnowledgeItem[];
      readonly workflows: readonly Workflow[];
    };

/**
 * Where a session's knowledge comes from. `session` rejects when the request fails: a failure
 * is not an answer (the rule `SearchSource` states).
 *
 * `keep` and `discard` are the owner's two writes on a draft (issue 204). They are optional: a
 * source without them offers no write, and then neither button is drawn, because a Keep that
 * reaches nothing would claim a decision the page cannot record. Each resolves once the source
 * has recorded it and rejects otherwise; the item changes only after it resolves.
 */
export interface SessionKnowledgeSource {
  session(sessionId: SessionId): Promise<SessionKnowledge>;
  keep?(sessionId: SessionId, itemId: string): Promise<void>;
  discard?(sessionId: SessionId, itemId: string): Promise<void>;
}

/** What `applyKnowledge` takes: since the org half went (issue 286), the session source itself. */
export type KnowledgeSource = SessionKnowledgeSource;

/** The source the side column reads, and whether its answers carry workflows. */
export interface KnowledgeSources {
  readonly session: SessionKnowledgeSource | null;
  /** `false` when the session source answers no workflows: Workflows stays `Coming soon`. */
  readonly workflows: boolean;
}

/**
 * The choice (issue 229). A source handed in through `applyKnowledge` (`applied`, `null`
 * included) answers the column. With none handed in, the column reads `route`, the derived
 * route's session read, which answers no workflows. `route` is `null` where there is no route
 * to ask (a session handed in by a fixture), and then the column has no source either.
 */
export function knowledgeSources(
  applied: KnowledgeSource | null | undefined,
  route: SessionKnowledgeSource | null,
): KnowledgeSources {
  if (applied !== undefined) return { session: applied, workflows: true };
  return { session: route, workflows: false };
}

/**
 * What a knowledge place knows: no source (`none`, `Coming soon`), a source that has not
 * answered yet, a read that failed, or the answer (decision 38, issue 204).
 */
export type KnowledgeView<T> = T | "none" | "unanswered" | "failed";

/** The side column's Knowledge and Workflows. */
export type SessionKnowledgeView = KnowledgeView<SessionKnowledge>;

/** A session's answer with the draft `itemId` kept (`keep`) or gone (`discard`). */
export function afterWrite(answer: SessionKnowledge, itemId: string, write: "keep" | "discard"): SessionKnowledge {
  if (answer.state !== "answered") return answer;
  const items =
    write === "discard"
      ? answer.items.filter((item) => item.id !== itemId)
      : answer.items.map((item) => (item.id === itemId ? { ...item, status: "kept" as const } : item));
  return { ...answer, items };
}
